import { useCallback } from "react";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

export type EducationalResourceAuditEvent =
  | "LIST_VIEW"
  | "SEARCH"
  | "ADD"
  | "EDIT"
  | "DELETE";

const EVENTS: Record<
  EducationalResourceAuditEvent,
  {
    option: OPTIONS;
    actionCode: ACTION_CODE;
    action: string;
    detail: (id?: string) => string;
  }
> = {
  LIST_VIEW: {
    option: OPTIONS.EDUCATIONAL_RESOURCES_LIST,
    actionCode: ACTION_CODE.WEB_EDU_RES_LIST_VIEW,
    action: "When entering",
    detail: () => "Viewed the list of educational resources",
  },
  SEARCH: {
    option: OPTIONS.EDUCATIONAL_RESOURCES_LIST,
    actionCode: ACTION_CODE.WEB_EDU_RES_SEARCH,
    action: "When searching",
    detail: () => "Searched within the list of educational resources",
  },
  ADD: {
    option: OPTIONS.EDUCATIONAL_RESOURCE_ADD,
    actionCode: ACTION_CODE.WEB_EDU_RES_ADD,
    action: "When adding",
    detail: (id) => `Created educational resource ${id}`,
  },
  EDIT: {
    option: OPTIONS.EDUCATIONAL_RESOURCE_EDIT,
    actionCode: ACTION_CODE.WEB_EDU_RES_EDIT,
    action: "When editing",
    detail: (id) => `Edited educational resource ${id}`,
  },
  DELETE: {
    option: OPTIONS.EDUCATIONAL_RESOURCE_DELETE,
    actionCode: ACTION_CODE.WEB_EDU_RES_DELETE,
    action: "When deleting",
    detail: (id) => `Deleted educational resource ${id}`,
  },
};

/**
 * Audit entries for the Educational resources module (BR-14). Call ONLY after the operation
 * succeeded. Details carry ids only — never titles, URLs or search text.
 */
export const useEducationalResourceAudit = () => {
  const createAuditLog = useCreateAuditlog();
  const { userInfo } = useUserStore();
  const userId = userInfo?._id;
  const { mutate } = createAuditLog;

  return useCallback(
    (event: EducationalResourceAuditEvent, targetId?: string) => {
      if (!userId) return;
      const config = EVENTS[event];
      mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EDUCATIONAL_RESOURCES,
        option: config.option,
        actionCode: config.actionCode,
        action: config.action,
        detail: config.detail(targetId),
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userId,
        ...(targetId ? { targetId } : {}),
      });
    },
    [mutate, userId]
  );
};
